<template>
  <div>
    <div class="row page-title-header">
      <div class="col-12">
        <div class="page-header">
          <b-breadcrumb class="m-0">
            <b-breadcrumb-item
              :to="{
                name: 'DashboardHome',
              }"
            >
              <i class="fa fa-home"></i>
            </b-breadcrumb-item>
            <b-breadcrumb-item active>組織資料中心</b-breadcrumb-item>
            <b-breadcrumb-item active>模組管理</b-breadcrumb-item>
          </b-breadcrumb>
        </div>
      </div>
    </div>
    <b-card>
      <div class="row d-flex mb-4 mb-xl-2 justify-content-between">
        <h4 class="col-12 col-xl-6 mb-2 mb-xl-0 font-weight-bold">
          {{ `【${organizationName}】模組管理` }}
        </h4>
        <div
          class="
            col-12 col-xl-6
            d-flex
            flex-column flex-xl-row
            align-items-start align-items-xl-center
          "
        >
          <b-button
            v-if="checkPermission([consts.MODULE_CREATE])"
            class="flex-shrink-0 mb-2 mb-xl-0 mr-2"
            variant="primary"
            @click="handleCreate"
          >
            <i class="fa fa-plus"></i>新增模組
          </b-button>
          <b-form-select
            v-model="query.merchant_id"
            :options="merchants"
            :disabled="isFetchMerchants"
            @change="handlePageResetWithoutReload"
            class="mb-2 mb-xl-0 mr-2"
          ></b-form-select>
          <b-input-group>
            <b-form-input
              placeholder="搜尋模組名稱"
              v-model="query.keyword"
              @keyup.enter="handlePageResetWithoutReload"
            ></b-form-input>
            <b-input-group-append>
              <b-button @click="handlePageResetWithoutReload">
                <i class="fa fa-search"></i>
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </div>
      </div>

      <b-table
        striped
        hover
        responsive
        :items="modules"
        :fields="fields"
        :busy="isLoading"
      >
        <template #table-busy>
          <div class="text-center my-2">
            <b-spinner class="align-middle"></b-spinner>
          </div>
        </template>
        <template #cell(name)="data">
          {{ data.item.config && data.item.config.name }}
          <BIconQuestionCircle
            class="d-inline-block"
            :id="`popover-tips-${data.item.id}`"
            v-if="data.item.config && data.item.config.description"
          ></BIconQuestionCircle>
          <b-popover
            :target="`popover-tips-${data.item.id}`"
            triggers="hover focus click"
          >
            <div class="text-left">
              {{ data.item.config && data.item.config.description }}
            </div>
          </b-popover>
        </template>
        <template #cell(actions)="data">
          <b-button
            v-if="
              data.item.status == 'inactivated' &&
              checkPermission([consts.MODULE_LIST_VIEW])
            "
            variant="primary"
            @click="handleActivate(data.item.module_code)"
          >
            開通
          </b-button>
          <b-button
            v-if="
              data.item.status != 'inactivated' &&
              checkPermission([consts.MODULE_MODIFY])
            "
            class="ml-2"
            variant="inverse-info"
            :to="{
              name: 'ModuleConfig',
              params: {
                orgId: orgId,
                moduleId: data.item.id,
              },
            }"
          >
            <span class="mdi mdi-settings"></span>
            設定
          </b-button>
          <b-button
            v-if="
              data.item.status != 'inactivated' &&
              checkPermission([consts.MODULE_LIST_VIEW])
            "
            class="ml-2"
            variant="inverse-primary"
            :to="{
              name: 'ModuleShow',
              params: {
                orgId: orgId,
                moduleId: data.item.id,
              },
            }"
          >
            <span class="mdi mdi-eye"></span>
            查看
          </b-button>
          <b-button
            v-if="
              data.item.status != 'inactivated' &&
              checkPermission([consts.MODULE_MODIFY])
            "
            class="ml-2"
            variant="inverse-warning"
            :to="{
              name: 'ModuleEdit',
              params: {
                orgId: orgId,
                moduleId: data.item.id,
              },
            }"
          >
            <span class="mdi mdi-lead-pencil"></span>
            編輯
          </b-button>
          <b-button
            v-if="
              data.item.status != 'inactivated' &&
              checkPermission([consts.MODULE_DELETE])
            "
            class="ml-2"
            variant="inverse-danger"
            :disabled="data.item.is_enabled === 1"
            @click="handleDelete(data.item.id, data.item.module_code)"
          >
            <span class="mdi mdi-delete"></span>
            刪除
          </b-button>
        </template>
      </b-table>

      <CustomPagination
        :currentPage="query.page"
        :totalRows="total"
        :perPage="query.per_page"
        @page-change="handlePageChange"
        @per-page-change="handlePerPageChange"
      />
    </b-card>
  </div>
</template>

<script>
import moduleApi from "../../../apis/module";
import { BIconQuestionCircle } from "bootstrap-vue";
import merchantApi from "../../../apis/merchant";
import * as consts from "@/consts";
import PermissionChecker from "@/utils/PermissionChecker";
import CustomPagination from "@/components/Page/Dashboard/CustomPagination.vue";
import { paginationMixin } from "@/mixins/pagination";
import { mapGetters } from "vuex";
import { updateQueryFromRoute, updateUrlWithoutReload } from "@/utils/updateUrl";

export default {
  components: {
    BIconQuestionCircle,
    CustomPagination,
  },
  mixins: [paginationMixin],
  data() {
    return {
      consts,
      isFetchMerchants: false,
      isLoading: false,
      modules: [],
      merchants: [{ value: null, text: "請選擇" }],
      fields: [
        {
          key: "merchant.name",
          label: "通路",
        },
        {
          key: "name",
          label: "模組名稱",
        },
        {
          key: "module_code",
          label: "代號",
        },
        {
          key: "status",
          label: "是否啟用",
          formatter: (value) => {
            switch (value) {
              case "inactivated":
                return "未開通";
              case "enabled":
                return "啟用";
              case "disabled":
                return "未啟用";
              case "disabled-but-not-in-config-mapping":
                return "未啟用(無設定檔)";
              case "enabled-but-not-in-config-mapping":
                return "啟用(無設定檔)";
              default:
                return "啟用(無設定檔)";
            }
          },
        },
        {
          key: "actions",
          label: "操作",
        },
      ],
      organizationName: "",
      total: 0,
      initialized: false,
      query: {
        page: 1,
        per_page: 10,
        keyword: "",
        merchant_id: null,
      },
    };
  },
  computed: {
    ...mapGetters("route", ["routeQuery"]),
    orgId() {
      return this.$route.params.orgId;
    },
    queryWatcher() {
      return `${this.query.page}-${this.query.per_page}`;
    },
  },
  watch: {
    queryWatcher: {
      handler() {
        if (!this.initialized) return;
        updateUrlWithoutReload(this.query, this.$store, this.$router);
      },
    },
    routeQuery() {
      if (!this.initialized) return;
      this.fetchModules();
    },
  },
  created() {
    this.query = updateQueryFromRoute(this.$route, this.$store, this.query);
  },
  async mounted() {
    if (!this.checkPermission([consts.MODULE_LIST_VIEW])) {
      this.$swal
        .fire({
          type: "error",
          text: "你沒有權限訪問此頁面",
          confirmButtonColor: "#d33",
        })
        .then(() => {
          this.$router.push({ name: "OrganizationList" });
        });
    } else {
      await this.getMerchants();
      await this.fetchModules();
      this.initialized = true;
    }
  },
  methods: {
    async fetchModules() {
      try {
        this.isLoading = true;
        const params = { ...this.query };
        if (params.merchant_id === "null") {
          params.scope = "no_merchant";
          delete params.merchant_id;
        } else if (params.merchant_id === "all_active") {
          delete params.merchant_id;
        }

        const { data } = await moduleApi.getModules(this.orgId, params);
        this.total = data.meta.total;
        this.modules = Object.values(data.data);
        this.organizationName = data.meta.organization_name;
      } catch (error) {
        console.log("");
      }
      this.isLoading = false;
    },
    async getMerchants() {
      this.isFetchMerchants = true;
      const { data } = await merchantApi.list();
      this.isFetchMerchants = false;

      if (data.data.length > 0) {
        const lineMerchant = data.data.find((m) => m.type === consts.MERCHANT_TYPES.LINE_TYPE);

        // 預設選取 LINE 官方帳號，若無則選品牌組織階層
        if (!this.query.merchant_id) {
          this.query.merchant_id = lineMerchant ? lineMerchant.id : "null";
        }

        this.merchants = [
          { value: "all_active", text: "全組織已開通模組" },
          { value: "null", text: "品牌組織階層" },
          ...data.data.map((merchant) => {
            return {
              value: merchant.id,
              text: merchant.type_name
                ? `${merchant.name} (${merchant.type_name})`
                : merchant.name,
            };
          }),
        ];
      } else {
        this.query.merchant_id = null;
        this.merchants = [
          { value: null, text: "請選擇" },
          { value: "null", text: "品牌組織階層" },
        ];
      }
    },
    async handleActivate(module_code) {
      if (!this.merchants.length) {
        this.$swal.fire({
          type: "error",
          text: "錯誤，請先建立通路",
          confirmButtonColor: "#d33",
        });
        return;
      }
      this.isLoading = true;
      const params = {
        module_code: module_code,
      };

      if (this.query.merchant_id && this.query.merchant_id !== "null") {
        params.merchant_id = this.query.merchant_id;
      }

      await moduleApi.activateModule(this.orgId, params);
      await this.fetchModules();
      this.isLoading = false;
    },
    async handleDelete(moduleId, module_code) {
      this.$swal
        .fire({
          type: "warning",
          title: "你確定要刪除嗎？",
          html: `
            <div class="d-block">
              <div class="my-3">
                <div>模組代號：${module_code}</div>
              </div>
              <div>刪除後，相關資料將無法回復，<br/>請您確認！</div>
            </div>`,
          confirmButtonText: "確定刪除",
          cancelButtonText: "不刪除",
          showCancelButton: true,
          confirmButtonColor: "#d33",
          cancelButtonColor: "#3085d6",
          reverseButtons: true,
        })
        .then(async (result) => {
          if (result.value) {
            try {
              this.isLoading = true;
              await moduleApi.deleteModule(this.orgId, moduleId);
              await this.getMerchants();
              await this.fetchModules(this.orgId);
            } catch (error) {
              console.log("");
            }
            this.isLoading = false;
          }
        });
    },
    handleCreate() {
      this.$router.push({
        name: "ModuleCreate",
      });
    },
    checkPermission(permissions) {
      const checker = new PermissionChecker();
      return checker.check(permissions);
    },
  },
};
</script>
